import React from 'react';
import { PropTypes } from 'prop-types';
import { compose, pure } from 'recompose';
import LoadSpinner from 'components/LoadSpinner';

import KeyBoardSelector from 'components/KeyBoardSelector';
import ItemAddress from 'components/form/AddressSelector/Items/Address';
import CreateAddress from 'components/form/AddressSelector/Items/CreateAddress';
import ModifyAddress from 'components/form/AddressSelector/Items/ModifyAddress';
import { AutoSizer, InfiniteLoader, List } from 'react-virtualized';
import { LIST, MODE } from './constants';
import 'react-virtualized/styles.css';

import ItemZipCodeQuote from 'components/form/AddressSelector/Items/ZipCodeQuote';

const enhance = compose(pure);

const AddressSelector = ({
  className,
  address,
  options,
  mode,
  selectAddress,
  loading,
  listenerElement,
  createAddress,
  modifyAddress,
  quoteWithZipcode,
  loadMoreAddresses,
  totalAddresses,
  hasError,
  handleIsRowLoaded,
  t,
}) => {
  const rowRenderer = ({ key, index, style }) => (
    <ItemAddress
      key={key}
      value={options[index]}
      style={style}
      onClick={selectAddress}
    />
  );

  return (
    <KeyBoardSelector listenerElement={listenerElement} className={className}>
      {/* No results */}
      {mode === MODE.SEARCH && !loading && options?.length === 0 && (
        <p className="address-selector__no-results">{t('empty_addresses')}</p>
      )}

      {/* Zipcode */}
      {mode === MODE.ZIPCODE && !loading && !hasError && (
        <ItemZipCodeQuote
          onClick={quoteWithZipcode}
          zipcode={address.zipcode}
          key={address.zipcode}
        />
      )}

      {/* Selected item */}
      {mode === MODE.SELECTED && !loading && !hasError && (
        <div className="address-selector__selected">
          <ItemAddress value={address} onClick={selectAddress} />
          <ModifyAddress onClick={modifyAddress} />
        </div>
      )}

      {/* List */}
      {mode === MODE.SEARCH && (
        <div className="address-selector__list">
          {options?.length > 0 && (
            <p className="address-selector__list-header">
              {t('saved_addresses')}
            </p>
          )}
          <AutoSizer disableHeight>
            {({ width }) =>
              options?.length > 0 && (
                <InfiniteLoader
                  isRowLoaded={handleIsRowLoaded}
                  loadMoreRows={loadMoreAddresses}
                  rowCount={totalAddresses}
                  threshold={20}
                  minimumBatchSize={10}
                >
                  {({ onRowsRendered, registerChild }) => (
                    <List
                      onRowsRendered={onRowsRendered}
                      ref={registerChild}
                      rowCount={totalAddresses}
                      rowRenderer={rowRenderer}
                      rowHeight={LIST.ROW_HEIGHT}
                      width={width}
                      height={LIST.LIST_HEIGHT}
                    />
                  )}
                </InfiniteLoader>
              )
            }
          </AutoSizer>
        </div>
      )}

      {/* Loading spinner */}
      {loading && (
        <div className="loading">
          <LoadSpinner
            loadText={t('load_text')}
            loadTitle={t('load_title')}
            skin="center"
          />
        </div>
      )}

      {/* Create address */}
      <CreateAddress onClick={createAddress} />
    </KeyBoardSelector>
  );
};

AddressSelector.propTypes = {
  className: PropTypes.string,
  address: PropTypes.shape({
    isZipcode: PropTypes.bool,
  }),
  selectAddress: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  listenerElement: PropTypes.instanceOf(Element).isRequired,
  createAddress: PropTypes.func.isRequired,
  modifyAddress: PropTypes.func.isRequired,
  quoteWithZipcode: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  inputValue: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object),
  mode: PropTypes.string.isRequired,
  loadMoreAddresses: PropTypes.func.isRequired,
  totalAddresses: PropTypes.number.isRequired,
  handleIsRowLoaded: PropTypes.func.isRequired,
};

AddressSelector.defaultProps = {
  loading: false,
  hasError: false,
  inputValue: '',
};

export default enhance(AddressSelector);
